class SurfaceAreaProduct extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-sa-variants]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      console.warn('[Surface Area] Variant data could not be read.', error);
      return;
    }

    this.mainColours = [...this.querySelectorAll('[data-sa-main-colour]')];
    this.packRadios = [...this.querySelectorAll('[data-sa-pack]')];
    this.bundleColours = [...this.querySelectorAll('[data-sa-bundle-colour]')];
    this.form = this.querySelector('[data-sa-bundle-form]');
    this.mainButton = this.querySelector('[data-sa-atc]');
    this.mobileButton = this.querySelector('[data-sa-mobile-button]');
    this.mobileBar = this.querySelector('[data-sa-mobile-atc]');
    this.expressCheckout = this.querySelector('[data-sa-express-checkout]');
    this.expressNote = this.querySelector('[data-sa-express-note]');
    this.expressVariant = this.querySelector('[data-sa-express-variant]');
    this.expressQuantity = this.querySelector('[data-sa-express-quantity]');

    this.mainColours.forEach((radio) => radio.addEventListener('change', () => {
      this.applyColourToBundle(radio.value);
      this.updateBundle();
      this.showGalleryForColour(radio.value);
    }));
    this.packRadios.forEach((radio) => radio.addEventListener('change', () => {
      this.updateBundle();
      this.applyColourToBundle(this.selectedMainColour());
      this.updateAvailability();
    }));
    this.bundleColours.forEach((select) => select.addEventListener('change', () => {
      this.paintBundleSwatch(select);
      if (Number(select.dataset.saBundleColour) === 1) this.syncMainColour(select.value);
      this.updateExpressCheckout(Number(this.selectedPack()?.dataset.saPackQuantity || 1));
      this.updateAvailability();
    }));

    this.form?.addEventListener('submit', (event) => this.addBundle(event));
    this.mobileButton?.addEventListener('click', () => this.mainButton?.click());

    if (this.mainButton && this.mobileBar) {
      const observer = new IntersectionObserver(([entry]) => {
        this.mobileBar.dataset.visible = String(!entry.isIntersecting);
      }, { threshold: 0.15 });
      observer.observe(this.mainButton);
    }

    this.updateDispatch();
    this.setupGallery();
    this.updateBundle();
  }

  selectedMainColour() {
    return this.mainColours.find((radio) => radio.checked)?.value || this.variants[0]?.colour;
  }

  selectedPack() {
    return this.packRadios.find((radio) => radio.checked);
  }

  updateBundle() {
    const selectedPack = this.selectedPack();
    if (!selectedPack) return;
    const quantity = Number(selectedPack.dataset.saPackQuantity || 1);
    const bundlePrice = Number(selectedPack.dataset.saPackPrice || 0);
    const offerName = selectedPack.dataset.saPackOffer || `${quantity} towels`;
    const regularPrice = Number(this.dataset.singlePrice || 0) * quantity;

    this.querySelectorAll('[data-sa-pack-card]').forEach((card) => {
      const active = card.contains(selectedPack);
      card.classList.toggle('is-selected', active);
      const colourPanel = card.querySelector('[data-sa-pack-colours]');
      if (colourPanel) colourPanel.hidden = !active;
      card.querySelectorAll('[data-sa-bundle-colour]').forEach((select) => { select.disabled = !active; });
    });

    this.querySelectorAll('[data-sa-price]').forEach((element) => { element.textContent = this.money(bundlePrice); });
    this.querySelectorAll('[data-sa-compare-price]').forEach((element) => {
      element.textContent = this.money(regularPrice);
      element.hidden = bundlePrice >= regularPrice;
    });
    this.querySelectorAll('[data-sa-sale-badge]').forEach((element) => { element.hidden = bundlePrice >= regularPrice; });
    this.querySelectorAll('[data-sa-afterpay]').forEach((element) => { element.setAttribute('data-amount', (bundlePrice / 100).toFixed(2)); });
    this.querySelectorAll('[data-sa-colour-label]').forEach((element) => { element.textContent = this.selectedMainColour() || ''; });
    this.querySelectorAll('[data-sa-pack-label]').forEach((element) => { element.textContent = offerName; });
    const buttonText = this.mainButton?.querySelector('[data-sa-atc-text]');
    if (buttonText) buttonText.textContent = `${this.dataset.addLabel} | ${this.money(bundlePrice)}`;
    this.updateExpressCheckout(quantity);
    this.updateButtonColour(this.selectedMainColour());
    this.updateShipping(bundlePrice);
    this.updateAvailability();
  }

  updateExpressCheckout(quantity) {
    if (!this.expressCheckout) return;
    const selections = this.activeColourSelects();
    const colours = [...new Set(selections.map((select) => select.value))];
    const singleColour = colours.length === 1;
    const variant = singleColour
      ? this.variants.find((item) => item.colour === colours[0] && item.available)
      : null;

    this.expressCheckout.hidden = !variant;
    if (this.expressNote) this.expressNote.hidden = singleColour;
    if (!variant) return;

    if (this.expressVariant) {
      this.expressVariant.value = String(variant.id);
      this.expressVariant.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.expressQuantity) {
      this.expressQuantity.value = String(quantity);
      this.expressQuantity.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  activeColourSelects() {
    return [...this.querySelectorAll('[data-sa-pack-card].is-selected [data-sa-bundle-colour]:not(:disabled)')];
  }

  applyColourToBundle(colour) {
    this.activeColourSelects().forEach((select) => {
      select.value = colour;
      this.paintBundleSwatch(select);
    });
  }

  syncMainColour(colour) {
    const radio = this.mainColours.find((item) => item.value === colour);
    if (radio) radio.checked = true;
    this.querySelectorAll('[data-sa-colour-label]').forEach((element) => { element.textContent = colour; });
    this.updateButtonColour(colour);
    this.showGalleryForColour(colour);
  }

  updateButtonColour(colour) {
    const palette = {
      'Ocean Blue': { background: '#6AA7DA', text: '#11110F', border: '#4F8FC5' },
      'Petal Pink': { background: '#DA6C93', text: '#11110F', border: '#C25179' },
      'Soft Black': { background: '#272629', text: '#FFFFFF', border: '#272629' }
    };
    const selected = palette[colour] || palette['Ocean Blue'];
    this.style.setProperty('--sa-atc-background', selected.background);
    this.style.setProperty('--sa-atc-text', selected.text);
    this.style.setProperty('--sa-atc-border', selected.border);
  }

  paintBundleSwatch(select) {
    const swatch = select.closest('.sa-bundle-colour__control')?.querySelector('[data-sa-bundle-swatch]');
    if (!swatch) return;
    const colours = { 'Ocean Blue': '#6AA7DA', 'Soft Black': '#272629', 'Petal Pink': '#DA6C93' };
    swatch.style.setProperty('--swatch', colours[select.value] || '#6AA7DA');
    const images = {
      'Ocean Blue': this.dataset.swatchOceanBlue,
      'Petal Pink': this.dataset.swatchPetalPink,
      'Soft Black': this.dataset.swatchSoftBlack
    };
    swatch.style.backgroundImage = images[select.value] ? `url("${images[select.value]}")` : 'none';
  }

  updateAvailability() {
    const unavailable = this.activeColourSelects().some((select) => !this.variants.find((variant) => variant.colour === select.value && variant.available));
    if (this.mainButton) {
      this.mainButton.disabled = unavailable;
      const buttonText = this.mainButton.querySelector('[data-sa-atc-text]');
      if (buttonText && unavailable) buttonText.textContent = this.dataset.soldLabel;
    }
    if (this.mobileButton) this.mobileButton.disabled = unavailable;
    if (unavailable && this.expressCheckout) this.expressCheckout.hidden = true;
  }

  async addBundle(event) {
    event.preventDefault();
    const error = this.querySelector('[data-sa-error]');
    const live = this.querySelector('[data-sa-live]');
    const selectedPack = this.selectedPack();
    const colourSelections = this.activeColourSelects();
    if (!selectedPack || !colourSelections.length) return;

    const grouped = new Map();
    for (const select of colourSelections) {
      const variant = this.variants.find((item) => item.colour === select.value && item.available);
      if (!variant) {
        this.showError(`${select.value} is currently unavailable.`);
        return;
      }
      grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1);
    }

    const quantity = Number(selectedPack.dataset.saPackQuantity || colourSelections.length);
    const offerName = selectedPack.dataset.saPackOffer || `${quantity}-towel set`;
    const items = [...grouped.entries()].map(([id, itemQuantity]) => ({
      id,
      quantity: itemQuantity,
      properties: {
        '_Surface Area offer': offerName,
        '_Offer quantity': String(quantity)
      }
    }));

    if (error) error.classList.add('hidden');
    this.setLoading(true);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'We could not add that set to your cart.');
      if (live) live.textContent = `${quantity} towels added to cart.`;
      const drawer = document.querySelector('sa-cart-drawer');
      if (drawer) {
        try {
          await Promise.race([
            (async () => {
              await customElements.whenDefined('sa-cart-drawer');
              await drawer.refresh();
              await customElements.whenDefined('theme-drawer');
              drawer.closest('theme-drawer').open();
            })(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Cart drawer timeout')), 8000)),
          ]);
          this.setLoading(false);
        } catch (_) {
          // The add succeeded: show the existing cart, never invite a duplicate retry.
          window.location.assign(`${root}cart`);
        }
      } else {
        window.location.assign(`${root}cart`);
      }
    } catch (requestError) {
      this.showError(requestError.message);
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (!this.mainButton) return;
    this.mainButton.disabled = loading;
    this.mainButton.setAttribute('aria-busy', String(loading));
    const buttonText = this.mainButton.querySelector('[data-sa-atc-text]');
    if (buttonText && loading) buttonText.textContent = 'Adding your towels...';
    if (!loading) this.updateBundle();
  }

  showError(message) {
    const error = this.querySelector('[data-sa-error]');
    if (!error) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }

  setupGallery() {
    this.gallerySlides = [...this.querySelectorAll('[data-sa-gallery-slide]')];
    this.galleryThumbs = [...this.querySelectorAll('[data-sa-gallery-thumb]')];
    this.galleryIndex = Math.max(0, this.gallerySlides.findIndex((slide) => slide.classList.contains('is-active')));
    this.galleryReady = false;
    this.galleryThumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showGallerySlide(Number(thumb.dataset.saGalleryThumb))));
    this.querySelector('[data-sa-gallery-prev]')?.addEventListener('click', () => this.showGallerySlide(this.galleryIndex - 1));
    this.querySelector('[data-sa-gallery-next]')?.addEventListener('click', () => this.showGallerySlide(this.galleryIndex + 1));
    this.showGallerySlide(this.galleryIndex);
    this.galleryReady = true;
  }

  showGalleryForColour(colour) {
    if (!this.gallerySlides?.length) return;
    const mediaId = String(this.variants.find((variant) => variant.colour === colour)?.mediaId || '');
    if (!mediaId) return;
    const index = this.gallerySlides.findIndex((slide) => String(slide.dataset.saMediaId) === mediaId);
    if (index >= 0) this.showGallerySlide(index);
  }

  showGallerySlide(index) {
    if (!this.gallerySlides?.length) return;
    this.galleryIndex = (index + this.gallerySlides.length) % this.gallerySlides.length;
    this.gallerySlides.forEach((slide, position) => {
      const active = position === this.galleryIndex;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
    });
    this.galleryThumbs?.forEach((thumb, position) => {
      const active = position === this.galleryIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
      if (active && this.galleryReady) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    const count = this.querySelector('[data-sa-gallery-current]');
    if (count) count.textContent = String(this.galleryIndex + 1);
  }

  updateShipping(bundlePrice) {
    const threshold = Number(this.dataset.shippingThreshold || 7500);
    const remaining = Math.max(0, threshold - bundlePrice);
    const progress = threshold > 0 ? Math.min(100, (bundlePrice / threshold) * 100) : 100;
    this.querySelectorAll('[data-sa-shipping-bar]').forEach((bar) => bar.style.setProperty('--sa-progress', `${progress}%`));
    const message = remaining === 0
      ? this.dataset.shippingUnlocked
      : this.dataset.shippingRemaining.replace('[amount]', this.money(remaining));
    this.querySelectorAll('[data-sa-shipping-copy]').forEach((element) => { element.textContent = message; });
  }

  updateDispatch() {
    const target = this.querySelector('[data-sa-dispatch-copy]');
    if (!target) return;
    const timeZone = this.dataset.timeZone || 'Australia/Perth';
    const cutoff = Number(this.dataset.cutoff || 13);
    const saturday = this.dataset.saturday === 'true';
    const now = new Date();
    const hour = Number(new Intl.DateTimeFormat('en-AU', { hour: '2-digit', hour12: false, timeZone }).format(now));
    const weekday = new Intl.DateTimeFormat('en-AU', { weekday: 'short', timeZone }).format(now);
    const canDispatch = (day) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(day) || (saturday && day === 'Sat');

    if (canDispatch(weekday) && hour < cutoff) {
      target.textContent = this.dataset.dispatchToday;
      return;
    }
    for (let offset = 1; offset <= 7; offset += 1) {
      const candidate = new Date(now.getTime() + offset * 86400000);
      const candidateDay = new Intl.DateTimeFormat('en-AU', { weekday: 'short', timeZone }).format(candidate);
      if (canDispatch(candidateDay)) {
        target.textContent = offset === 1
          ? this.dataset.dispatchTomorrow
          : this.dataset.dispatchDay.replace('[day]', new Intl.DateTimeFormat('en-AU', { weekday: 'long', timeZone }).format(candidate));
        return;
      }
    }
  }

  money(cents) {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: this.dataset.currency || 'AUD' }).format(Number(cents) / 100);
  }
}

if (!customElements.get('surface-area-product')) {
  customElements.define('surface-area-product', SurfaceAreaProduct);
}
